import { useState, useEffect, useMemo } from "react";
import {
    Alert, Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent,
    DialogTitle, Divider, FormControl, InputLabel, MenuItem, Paper, Select,
    Table, TableBody, TableCell, TableContainer, TablePagination, TableRow, TextField
} from "@mui/material";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import api from "../../api";
import { useDebounce } from "../../hooks/useDebounce";
import {
    SnackBarComponent, getComparator, EnhancedTableHead, EnhancedTableToolbar,
    TableLoadingState, DeleteConfirmDialog, ImagePreviewCell, ExternalLinkCell,
    StatusChip, StatusToggleSwitch, ImageUploadSection
} from "../../components";

dayjs.extend(advancedFormat);
dayjs.extend(customParseFormat);

const headCells = [
    { id: "tag", numeric: false, disablePadding: true, label: "Tag" },
    { id: "title", numeric: false, disablePadding: false, label: "Title" },
    {
        id: "publishedOn",
        numeric: false,
        disablePadding: false,
        label: "Published On",
    },
    {
        id: "publicationName",
        numeric: false,
        disablePadding: false,
        label: "Publication Name",
    },
    {
        id: "resourcelink",
        numeric: false,
        disablePadding: false,
        label: "Resource Link",
    },
    {
        id: "desktopimage",
        numeric: false,
        disablePadding: false,
        label: "Desktop Image",
    },
    {
        id: "mobileimage",
        numeric: false,
        disablePadding: false,
        label: "Mobile Image",
    },
    {
        id: "status",
        numeric: false,
        disablePadding: false,
        label: "Status",
    },
    /*{
      id: "action",
      numeric: false,
      disablePadding: false,
      label: "",
    },*/
];



export default function Stories() {
    const [rows, setRows] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [debouncedSearchQuery] = useDebounce(searchQuery, 300);
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("tag");
    const [selected, setSelected] = useState([]);
    const [page, setPage] = useState(0);
    const [dense, setDense] = useState(true);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [desktopImg, setDesktopImg] = useState([]);
    const [mobileImg, setMobileImg] = useState([]);
    // snackbar
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    // onChange={(date) => setStartDate(date)}
    // const [publishedOn, setPublishedOn] = React.useState();
    // dialog box
    const [open, setOpen] = useState(false);
    const [scroll, setScroll] = useState("paper");
    const [isLoading, setIsLoading] = useState(true); // Initial loading state
    const [tags, setTags] = useState([]);

    const [newData, setNewData] = useState({
        tag: "",
        title: "",
        publishedOn: "",
        publicationName: "",
        resourceLink: "",
        desktopImage: "",
        mobileImage: "",
        desktopImageUrl: "",
        mobileImageUrl: "",
        status: "",
    });
    // update | edit
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [errorMessage, setErrorMessage] = useState(null);

    // State for Delete Confirmation Dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    const handleInputChange = (e) => {
        setNewData({ ...newData, [e.target.name]: e.target.value });
    };

    const handleDateChange = (date) => {
        setNewData((prevState) => ({
            ...prevState,
            publishedOn: date ? date.format("Do MMMM YYYY") : "",
        }));
    };

    const handleClickOpen = (scrollType) => () => {
        setOpen(true);
        setScroll(scrollType);
        setIsEditing(false);
        setNewData({
            tag: "",
            title: "",
            publishedOn: "",
            publicationName: "",
            resourceLink: "",
            desktopImage: "",
            mobileImage: "",
            desktopImageUrl: "",
            mobileImageUrl: "",
            status: "Hidden",
        });
        setDesktopImg([]);
        setMobileImg([]);
    };

    const handleEditClick = () => {
        const selectedRow = rows.find((row) => row.id === selected[0]);
        const hasImage = selectedRow.image;
        setNewData({
            tag: selectedRow.tag,
            title: selectedRow.title,
            publishedOn: selectedRow.publishedOn,
            publicationName: selectedRow.publicationName,
            resourceLink: selectedRow.resourceLink,
            desktopImageUrl: hasImage ? selectedRow.image.desktop || "" : "",
            mobileImageUrl: hasImage ? selectedRow.image.mobile || "" : "",
            status: selectedRow.status,
        });
        setDesktopImg(hasImage && selectedRow.image.desktop ? [
            {
                source: selectedRow.image.desktop,
                options: { type: 'url' },
            },
        ] : []);
        setMobileImg(hasImage && selectedRow.image.mobile ? [
            {
                source: selectedRow.image.mobile,
                options: { type: 'url' },
            },
        ] : []);
        // Store compound key "country_code#created_at" as editingId
        setEditingId(`${selectedRow.country_code}#${selectedRow.created_at}`);
        setIsEditing(true);
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleSubmit = async () => {
        try {
            if (
                !newData.tag ||
                !newData.title ||
                !newData.publishedOn ||
                !newData.publicationName ||
                !newData.resourceLink
            ) {
                setSnackbarMessage("Please fill all required fields!");
                setSnackbarSeverity("warning");
                setOpenSnackbar(true);
                return;
            }
            // Validate images
            if (
                (!desktopImg || desktopImg.length === 0 || (isEditing ? false : !desktopImg[0].file)) &&
                !newData.desktopImageUrl
            ) {
                setSnackbarMessage("Please provide a desktop image (URL or upload)!");
                setSnackbarSeverity("warning");
                setOpenSnackbar(true);
                return;
            }

            // Create a FormData object to send files and form data together
            const formData = new FormData();

            // Append text data
            formData.append("tag", newData.tag);
            formData.append("title", newData.title);
            formData.append("publishedOn", newData.publishedOn);
            formData.append("publicationName", newData.publicationName);
            formData.append("resourceLink", newData.resourceLink);
            formData.append("status", newData.status);
            if (newData.desktopImageUrl !== undefined && newData.desktopImageUrl !== null) {
                formData.append("desktopImageUrl", newData.desktopImageUrl);
            }
            if (newData.mobileImageUrl !== undefined && newData.mobileImageUrl !== null) {
                formData.append("mobileImageUrl", newData.mobileImageUrl);
            }
            // Append images if they exist
            if (desktopImg.length > 0 && desktopImg[0].file && typeof desktopImg[0].source !== "string") {
                // setDesktopImg()
                formData.append("desktopImage", desktopImg[0].file);
            }
            if (mobileImg.length > 0 && mobileImg[0].file && typeof mobileImg[0].source !== "string") {
                formData.append("mobileImage", mobileImg[0].file);
            }
            const token = localStorage.getItem("accessToken");
            // Decide whether to create or update data
            if (isEditing) {
                // Extract compound key parts from editingId
                const [country_code, created_at] = editingId.split('#');
                formData.append("country_code", country_code);
                formData.append("created_at", created_at);
                // Update existing data
                await api.put(
                    `/api/stories`,
                    formData,
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                // Show success snackbar
                setSnackbarMessage("Changes saved successfully!");
                setSnackbarSeverity("success");
            } else {
                // Create new data
                await api.post("/api/stories", formData, {
                    headers: {
                        Authorization: `Bearer ${token}`, // Set the Authorization header
                    },
                    //headers: { "Content-Type": "multipart/form-data" },
                });

                // Show success snackbar
                setSnackbarMessage("News added successfully!");
                setSnackbarSeverity("success");
            }

            setOpenSnackbar(true);

            // Refresh the table data after submission or edit
            fetchData();

            // Reset form after submission
            setOpen(false); // Close the dialog box
            setSelected([]); // Clear selected items
            setNewData({
                tag: "",
                title: "",
                publishedOn: "",
                publicationName: "",
                resourceLink: "",
            }); // Reset input fields
            setDesktopImg([]); // Clear desktop image
            setMobileImg([]); // Clear mobile image
        } catch (error) {
            console.error(
                "Error creating/editing data:",
                error.response?.data || error
            );

            // Show error snackbar
            setSnackbarMessage(error.response.data.message);
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const fetchData = async () => {
        setIsLoading(true);
        const token = localStorage.getItem("accessToken");
        let allItems = [];
        let nextPageKey = null;

        try {
            do {
                const response = await api.get("/api/stories", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    params: {
                        limit: 50,
                        pageKey: nextPageKey ? encodeURIComponent(JSON.stringify(nextPageKey)) : undefined,
                    },
                });

                const data = response.data.items || response.data; // Handle both paginated and non-paginated responses just in case
                if (data && data.length > 0) {
                    const mappedData = data.map(item => ({ ...item, id: `${item.country_code}#${item.created_at}` }));
                    allItems = [...allItems, ...mappedData];
                    setRows([...allItems]); // Update state iteratively for instant feedback
                }

                // Extract nextPageKey if available from backend
                nextPageKey = response.data.nextPageKey ? JSON.parse(decodeURIComponent(response.data.nextPageKey)) : null;

            } while (nextPageKey);

            setRows(allItems);
            setIsLoading(false); // Stop loading spinner
        } catch (error) {
            console.error("Error fetching data:", error);
            const { status, data } = error.response || {};
            const message = data ? data.message : "Error fetching data";
            setErrorMessage(message);
            setIsLoading(false);
        }
    };

    const fetchTags = async () => {
        const token = localStorage.getItem("accessToken");
        try {
            const response = await api.get("/api/tags", {
                headers: {
                    Authorization: `Bearer ${token}`, // Set the Authorization header
                },
            });
            // Check if the response status is 200 or 201 and data is empty
            setTags(response.data.data);
        } catch (error) {
            console.error("Error fetching data:", error);
            const { status, data } = error.response;
            const { message } = data;
            setErrorMessage(message);
        }
    };

    // More option code
    const ITEM_HEIGHT = 48;

    const [anchorEl, setAnchorEl] = useState(null);
    const openMore = Boolean(anchorEl);
    const handleMoreClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMoreClose = () => {
        setAnchorEl(null);
    };

    useEffect(() => {
        fetchData();
        fetchTags();
    }, []);

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === "asc";
        setOrder(isAsc ? "desc" : "asc");
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelecteds = rows.map((n) => n.id);
            setSelected(newSelecteds);
            return;
        }
        setSelected([]);
    };

    const handleClick = (event, id) => {
        const selectedIndex = selected.indexOf(id);
        let newSelected = [];

        if (selectedIndex === -1) {
            newSelected = newSelected.concat(selected, id);
        } else if (selectedIndex === 0) {
            newSelected = newSelected.concat(selected.slice(1));
        } else if (selectedIndex === selected.length - 1) {
            newSelected = newSelected.concat(selected.slice(0, -1));
        } else if (selectedIndex > 0) {
            newSelected = newSelected.concat(
                selected.slice(0, selectedIndex),
                selected.slice(selectedIndex + 1)
            );
        }

        setSelected(newSelected);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Reset page when searching
    useEffect(() => {
        setPage(0);
    }, [searchQuery]);

    const isSelected = (id) => selected.indexOf(id) !== -1;

    const emptyRows =
        page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;

    // Handle Delete Confirmation Dialog
    const handleDeleteClick = () => {
        setOpenDeleteDialog(true);
    };

    const handleDeleteConfirm = async () => {
        const token = localStorage.getItem("accessToken");
        try {
            await Promise.all(
                selected.map((id) => {
                    const [country_code, created_at] = id.split('#');
                    return api.delete(`/api/stories`, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        data: { country_code, created_at: Number(created_at) },
                    });
                })
            );
            fetchData();
            setSelected([]);
            setOpenDeleteDialog(false);

            // Show success snackbar
            setSnackbarMessage("Deleted successfully!");
            setSnackbarSeverity("success");
            setOpenSnackbar(true);
        } catch (error) {
            console.error("Error deleting data:", error);

            // Show error snackbar
            setSnackbarMessage(error.response?.data?.message || "Failed to delete.");
            setSnackbarSeverity("error");
            setOpenSnackbar(true);
        }
    };

    const handleSnackbarClose = () => {
        setOpenSnackbar(false); // Reset the state to close the snackbar
    };

    const handleDeleteCancel = () => {
        setOpenDeleteDialog(false);
    };
    const [tagError, setTagError] = useState(true);

    const filteredRows = useMemo(() => {
        if (!debouncedSearchQuery) return rows;
        const lowerQuery = debouncedSearchQuery.toLowerCase();
        return rows.filter((row) => {
            const tagMatch = (row.tag?.toLowerCase() || "").includes(lowerQuery);
            const titleMatch = (row.title?.toLowerCase() || "").includes(lowerQuery);
            return tagMatch || titleMatch;
        });
    }, [rows, debouncedSearchQuery]);

    const visibleRows = useMemo(
        () => {
            return filteredRows
                .sort(getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
        },
        [order, orderBy, page, rowsPerPage, filteredRows]
    );

    return (
        <Box sx={{ width: "100%" }}>
            {/* Snack bar */}
            {openSnackbar && (
                <SnackBarComponent
                    message={snackbarMessage}
                    severity={snackbarSeverity}
                    onClose={handleSnackbarClose} // Handle the snackbar close
                />
            )}

            <Paper sx={{ width: "100%", mb: 2 }}>
                <EnhancedTableToolbar
                    title="STORIES"
                    numSelected={selected.length}
                    handleClickOpen={handleClickOpen}
                    handleEditClick={handleEditClick}
                    handleDeleteClick={handleDeleteClick}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
                <TableContainer>
                    <Table
                        sx={{ minWidth: 750 }}
                        aria-labelledby="tableTitle"
                        size={dense ? "small" : "medium"}
                    >
                        <EnhancedTableHead
                            headCells={headCells}
                            numSelected={selected.length}
                            order={order}
                            orderBy={orderBy}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows.length}
                        />
                        <TableBody>
                            {isLoading ? (
                                <TableLoadingState
                                    isLoading={isLoading}
                                    errorMessage={errorMessage}
                                    colSpan={10}
                                />
                            ) : visibleRows.length === 0 ? (
                                <>
                                    {errorMessage != null ? (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center">
                                                <Alert severity="error">{errorMessage}</Alert>{" "}
                                            </TableCell>
                                        </TableRow>
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={10} align="center">
                                                <Alert severity="info">No data available.</Alert>{" "}
                                            </TableCell>
                                        </TableRow>
                                    )
                                    }

                                </>
                            ) : (
                                visibleRows.map((row, index) => {
                                    const isItemSelected = selected.includes(row.id);
                                    const labelId = `enhanced-table-checkbox-${index}`;
                                    return (
                                        <TableRow
                                            hover
                                            onClick={(event) => handleClick(event, row.id)}
                                            role="checkbox"
                                            aria-checked={isItemSelected}
                                            tabIndex={-1}
                                            key={row.id}
                                            selected={isItemSelected}
                                            sx={{ cursor: "pointer" }}
                                        >
                                            <TableCell padding="checkbox">
                                                <Checkbox
                                                    color="primary"
                                                    checked={isItemSelected}
                                                    inputProps={{ "aria-labelledby": labelId }}
                                                />
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                id={labelId}
                                                scope="row"
                                                padding="none"
                                            >
                                                {row.tag}
                                            </TableCell>
                                            <TableCell>{row.title}</TableCell>
                                            <TableCell>{row.publishedOn}</TableCell>
                                            <TableCell>{row.publicationName}</TableCell>
                                            <ExternalLinkCell href={row.resourceLink} />
                                            <ImagePreviewCell imageUrl={row.image?.desktop} alt="Desktop Image" />
                                            <ImagePreviewCell imageUrl={row.image?.mobile} alt="Mobile Image" />
                                            <TableCell><StatusChip status={row.status} /></TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                            {emptyRows > 0 && (
                                <TableRow style={{ height: (dense ? 33 : 53) * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={rows.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog
                open={open}
                onClose={handleClose}
                scroll={scroll}
                aria-labelledby="dialog-title"
                fullWidth
            >
                <DialogTitle id="dialog-title">
                    {isEditing ? "Edit" : "Add"}
                </DialogTitle>
                <DialogContent>
                    <Box
                        component="form"
                        sx={{ "& > :not(style)": { mt: 1.5, mb: 1.5, width: "100%" } }}
                        noValidate
                        autoComplete="off"
                    >
                        <FormControl fullWidth>
                            <InputLabel id="demo-simple-select-label">Tag</InputLabel>
                            <Select
                                labelId="demo-simple-select-label"
                                id="demo-simple-select"
                                label="Tag"
                                name="tag"
                                value={newData.tag}
                                onChange={handleInputChange}
                            >
                                {tags.map((element) => {
                                    return <MenuItem value={element.tag_title}>{element.tag_title}</MenuItem>
                                })
                                }
                            </Select>
                        </FormControl>
                        <TextField
                            margin="dense"
                            name="title"
                            label="Title"
                            type="text"
                            fullWidth
                            value={newData.title}
                            onChange={handleInputChange}
                        />
                        <TextField
                            margin="dense"
                            name="publicationName"
                            label="Publication Name"
                            type="text"
                            fullWidth
                            value={newData.publicationName}
                            onChange={handleInputChange}
                        />
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DemoContainer components={['DatePicker']}>
                                <DatePicker
                                    label="Date Of Publish"
                                    value={newData.publishedOn ? dayjs(newData.publishedOn, "Do MMMM YYYY") : null}
                                    onChange={handleDateChange}
                                    sx={{ width: '100%' }}
                                />
                            </DemoContainer>
                        </LocalizationProvider>
                        <TextField
                            margin="dense"
                            name="resourceLink"
                            label="Resource Link"
                            placeholder="https://example.com/resource"
                            type="text"
                            fullWidth
                            value={newData.resourceLink}
                            onChange={handleInputChange}
                        />
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                        <StatusToggleSwitch
                            isEditing={isEditing}
                            status={newData.status}
                            onChange={(val) => setNewData({ ...newData, status: val })}
                        />
                    </Box>
                    <Divider sx={{ '& > :not(style)': { mt: 1.5, mb: 1.5, width: '100%' } }}>
                        <Chip label="Upload Images" size="small" />
                    </Divider>
                    <ImageUploadSection
                        desktopImageUrl={newData.desktopImageUrl}
                        mobileImageUrl={newData.mobileImageUrl}
                        desktopImg={desktopImg}
                        mobileImg={mobileImg}
                        onChangeDesktopUrl={handleInputChange}
                        onChangeMobileUrl={handleInputChange}
                        onUpdateDesktopFiles={setDesktopImg}
                        onUpdateMobileFiles={setMobileImg}
                    />
                </DialogContent>
                <DialogActions sx={{ "& > :not(style)": { m: 1.5, width: "100%" } }}>
                    <Button variant="contained" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="contained" onClick={handleSubmit}>
                        {isEditing ? "Save" : "Add"}
                    </Button>
                </DialogActions>
            </Dialog>

            <DeleteConfirmDialog
                open={openDeleteDialog}
                selectedCount={selected.length}
                onCancel={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
            />
        </Box>
    );
}